import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const navLinks = [
    { labelKey: 'nav.home', path: '/' },
    { labelKey: 'nav.about', path: '/about' },
    { labelKey: 'nav.tours', path: '/tours' },
    { labelKey: 'nav.gallery', path: '/gallery' },
    { labelKey: 'nav.testimonials', path: '/testimonials' },
    { labelKey: 'nav.certifications', path: '/certifications' },
    { labelKey: 'nav.contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isTransparent = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        isTransparent ? 'bg-transparent' : 'bg-white border-b border-gray-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="w-12 h-12 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/3294a4af19582454411e375cbd1c78be/9871cfdc49cd769f3d8dc69b76a1630b.png"
              alt="Arus Travel & Tours"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="hidden sm:block">
            <p className={`font-bold text-base leading-tight whitespace-nowrap font-playfair transition-colors duration-300 ${isTransparent ? 'text-white' : 'text-emerald-800'}`}>
              Arus Travel & Tours
            </p>
            <p className={`text-xs transition-colors duration-300 ${isTransparent ? 'text-white/70' : 'text-gray-500'}`}>
              Sdn Bhd
            </p>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  isActive
                    ? isTransparent
                      ? 'text-amber-300 font-semibold'
                      : 'text-emerald-700 font-semibold'
                    : isTransparent
                    ? 'text-white/90 hover:text-white'
                    : 'text-gray-600 hover:text-emerald-700'
                }`}
              >
                {t(link.labelKey)}
              </Link>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-2">
          <LanguageSwitcher isTransparent={isTransparent} />
          <a
            href="tel:+60176459709"
            className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
              isTransparent ? 'text-white/90' : 'text-gray-600'
            }`}
          >
            <i className="ri-phone-line mr-1"></i>017 645 9709
          </a>
          <a
            href="https://www.easybook.com/la-la/bus/operator"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer border ${
              isTransparent
                ? 'border-white/60 text-white hover:bg-white/15'
                : 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            <i className="ri-bus-2-line"></i>{t('nav.busTickets')}
          </a>
          <Link
            to="/tours"
            className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('nav.bookNow')}
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
            isTransparent ? 'text-white' : 'text-gray-700'
          }`}
          aria-label="Toggle menu"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100">
          <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                    isActive
                      ? 'text-emerald-700 bg-emerald-50 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-emerald-700'
                  }`}
                >
                  {t(link.labelKey)}
                </Link>
              );
            })}
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
              <div className="px-4">
                <LanguageSwitcher />
              </div>
              <a href="tel:+60176459709" className="text-sm text-gray-600 px-4">
                <i className="ri-phone-line mr-2"></i>017 645 9709
              </a>
              <a
                href="https://www.easybook.com/la-la/bus/operator"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="border border-emerald-600 text-emerald-700 text-sm font-semibold px-5 py-2.5 rounded-md text-center cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-bus-2-line"></i>{t('nav.busTickets')}
              </a>
              <Link
                to="/tours"
                className="bg-amber-500 text-white text-sm font-semibold px-5 py-2.5 rounded-md text-center cursor-pointer whitespace-nowrap"
              >
                {t('nav.bookNow')}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
